/**
 *  @class
 *  @function Pagination - Scroll infinito para blog y colecciones
 */

// Restauración de posición de scroll al volver de una ficha de producto (botón "atrás").
// Shopify sirve las colecciones con `Cache-Control: private, no-store` (confirmado con
// curl -I contra newgardenshop.es); Safari/WebKit respeta ese header y desactiva el
// bfcache, así que "atrás" en iOS siempre recarga desde servidor en la página 1, perdiendo
// todo lo cargado por scroll infinito. RESTORE_AUTO_CAP limita cuántas tandas se
// recargan automáticamente (más allá de eso se ofrece un botón "Continuar donde lo
// dejaste" en vez de una ráfaga de fetches) — medido en producción (newgardenshop.es,
// 2026-08-07): ~0.67MB por tanda vía `?section_id=`, con products_per_page real de 36-48
// según colección, así que 3 tandas ronda los ~2MB, el mismo presupuesto que se usó para
// fijar este número. Si cambia el peso de imágenes por producto o el products_per_page en
// este tema, volver a medir (`curl -s -o /dev/null -w '%{size_download}' "<url>?page=2&section_id=<id>"`)
// en vez de reutilizar el valor a ciegas.
const PAGINATION_RESTORE_AUTO_CAP = 3;
const PAGINATION_RESTORE_TTL_MS = 30 * 60 * 1000; // 30 min

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      
      // Configuración general
      this.contentType = this.dataset.contentType || 'generic';
      this.currentPage = parseInt(this.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.dataset.totalPages) || 1;
      
      // Buscar el contenedor de items
      // Primero intentar con el ID de la sección, luego con selectores genéricos
      this.grid = document.querySelector('[data-id="' + id + '"]') ||
                  document.querySelector('#product-grid') ||
                  document.querySelector('.blog-section .row') ||
                  document.querySelector('ul.products.collection') ||
                  document.querySelector('.products.collection');
      
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      this.i = this.currentPage + 1; // Siguiente página a cargar
      
      // Debug info
      // console.log('Pagination initialized:', {
      //   id: id,
      //   contentType: this.contentType,
      //   currentPage: this.currentPage,
      //   totalPages: this.totalPages,
      //   nextPage: this.i,
      //   button: this.button,
      //   grid: this.grid,
      //   hasMore: this.currentPage < this.totalPages
      // });
    }
    
    async connectedCallback() {
      // Si no hay más páginas, mostrar mensaje y salir
      if (this.currentPage >= this.totalPages) {
        //console.log('No more pages available');
        this.endPagination();
        return;
      }

      // Remover la clase visually-hidden si hay más páginas
      if (this.button && this.button.classList.contains('visually-hidden')) {
        this.button.classList.remove('visually-hidden');
      }

      // Restaurar tandas cargadas previamente (si el usuario vuelve de una ficha de
      // producto) ANTES de armar el IntersectionObserver en vivo, para no lanzar fetches
      // de restauración y de scroll normal en paralelo.
      await this.restoreScrollState();

      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }

      window.addEventListener('scroll', throttle(() => this.saveScrollState(), 500), {
        passive: true
      });
    }
    
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;
      
      if (search) {
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);
        if (params === search) {
          params += '&' + newParam;
        }
      }
      return params;
    }
    
    loadMore() {
      let base = this;
      this.button.addEventListener('click', function(e) {
        e.preventDefault();
        base.loadProducts();
        this.blur();
        return false;
      });
    }
    
    infinite() {
      let base = this;
      
      // Crear un elemento sentinela al final del grid para detectar cuando está visible
      if (!base.sentinel) {
        base.sentinel = document.createElement('div');
        base.sentinel.className = 'pagination-sentinel';
        base.sentinel.style.height = '1px';
        base.sentinel.style.width = '100%';
        base.sentinel.style.visibility = 'hidden';
        base.sentinel.style.pointerEvents = 'none';
        
        // Colocar el sentinela justo antes del elemento de paginación
        if (base.grid && base.grid.parentNode) {
          base.grid.parentNode.insertBefore(base.sentinel, base);
        } else {
          base.insertAdjacentElement('beforebegin', base.sentinel);
        }
      }
      
      base.observer = new IntersectionObserver(function(entries) {
        entries.forEach(entry => {
          // Cuando el sentinela o el elemento de paginación es visible, cargar más productos
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            // Verificar que no estemos ya cargando y que haya más páginas
            if (!base.getAttribute('loading') && base.i <= base.totalPages) {
              base.loadProducts();
            }
          }
        });
      }, {
        root: null,
        rootMargin: '200px', // Cargar cuando esté a 200px de ser visible (más anticipación)
        threshold: [0, 0.1, 0.5, 1]
      });
      
      // Observar el elemento de paginación y el sentinela
      base.observer.observe(base);
      if (base.sentinel) {
        base.observer.observe(base.sentinel);
      }
    }
    
    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');

      if (base.getAttribute('loading')) {
        //console.log('Already loading, skipping...');
        return Promise.resolve();
      }

      // Verificar si hay más páginas
      if (this.i > this.totalPages) {
        //console.log('No more pages to load');
        this.endPagination();
        return Promise.resolve();
      }

      // Pedir solo la sección del grid en vez de la página completa (Shopify soporta
      // ?section_id=<id> para renderizar únicamente esa sección) — medido en producción:
      // ~4x menos peso por tanda (2.8MB → 0.7MB), misma estructura de DOM (#product-grid,
      // li.column/li.colx), así que renderProducts() no necesita ningún cambio para
      // seguir parseándola. Beneficia tanto al scroll normal como a restoreUpTo().
      if (base.dataset.section) {
        url += (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(base.dataset.section);
      }

      //console.log('Loading page:', this.i, 'of', this.totalPages, 'URL:', url);

      base.setAttribute('loading', 'true');

      return fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.text();
        })
        .then((responseText) => {
          //console.log('Response received, parsing...');
          base.renderProducts(responseText, url);
          dispatchCustomEvent('pagination:page-changed', {
            url: url,
            page: base.i,
            type: base.contentType
          });
          // Incrementar para la siguiente carga después de renderizar
          base.i++;
          base.saveScrollState();
        })
        .catch((error) => {
          console.error('Error loading more content:', error);
          base.removeAttribute('loading');
          base.showError();
        });
    }
    
    renderProducts(html, url) {
      let base = this;
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      let items = [];
      if (this.contentType === 'blog') {
        const blogSection = doc.querySelector('.blog-section');
        if (!blogSection) {
          //console.log('No blog section found in response');
          this.endPagination();
          return;
        }
        const grid_to_replace = blogSection.querySelector('.row');
        if (!grid_to_replace) {
          //console.log('No .row found in blog section');
          this.endPagination();
          return;
        }
        items = grid_to_replace.querySelectorAll('.columns');
      } else {
        // Para productos, buscar el grid de varias formas posibles
        const productGrid = doc.querySelector('#product-grid') || 
                           doc.querySelector('[id^="product-grid"]') ||
                           doc.querySelector('ul.products.collection') ||
                           doc.querySelector('.products.collection');
        
        if (!productGrid) {
          //console.log('No product grid found in response');
          this.endPagination();
          return;
        }
        
        // Obtener los elementos li dentro del grid
        items = productGrid.querySelectorAll('li.column, li.colx');
        
        // Si no encuentra con querySelectorAll, intentar con children
        if (items.length === 0) {
          const children = Array.from(productGrid.children);
          items = children.filter(child => child.tagName === 'LI');
        }
      }
      
      // Convertir NodeList/HTMLCollection a Array para mejor manejo
      items = Array.from(items);
      
      //console.log('Found items in response:', items.length);
      //console.log('Items HTML sample:', items.length > 0 ? items[0].outerHTML.substring(0, 200) : 'No items');

      if (items.length === 0) {
        //console.log('No items found, ending pagination');
        this.endPagination();
        return;
      }

      // Verificar que el grid existe en el DOM actual
      if (!this.grid) {
        console.error('Product grid not found in current DOM');
        this.endPagination();
        return;
      }

      // Clonar y añadir cada elemento
      const addedItems = [];
      for (let i = 0; i < items.length; i++) {
        const clonedPost = items[i].cloneNode(true);
        this.grid.appendChild(clonedPost);
        addedItems.push(clonedPost);
        //console.log('Added item:', i + 1, clonedPost.querySelector('h5') ? clonedPost.querySelector('h5').textContent.trim() : '');
      }
      
      // Mover el sentinela justo antes del elemento de paginación después de agregar items
      if (this.sentinel) {
        // Remover el sentinela de su posición actual si existe
        if (this.sentinel.parentNode) {
          this.sentinel.parentNode.removeChild(this.sentinel);
        }
        // Reinsertar el sentinela justo antes del elemento de paginación
        if (this.grid && this.grid.parentNode) {
          this.grid.parentNode.insertBefore(this.sentinel, this);
        } else {
          this.insertAdjacentElement('beforebegin', this.sentinel);
        }
      }
      
      // Reinicializar lazy loading
      this.initializeLazyLoading(addedItems);

      // WSAIO (descuentos wholesale) define su collections() dentro de un closure
      // privado (window.wsaio_execute_custom_code) — no existe window.collections que
      // se pueda llamar directamente. La única superficie pública es su propio parche
      // de history.pushState/replaceState: WSAIO envuelve ambos métodos globales para
      // relanzar collections() 2s después de cualquier cambio de historial en páginas de
      // colección/búsqueda. Reutilizamos ese hook pasando la URL actual sin modificarla
      // (mismo pathname+search que ya está en la barra de direcciones) — dispara el
      // parche sin cambiar nada visible ni reintroducir el bug de `?page=N`
      // acumulándose que rompía la URL en iOS. collections() es idempotente (salta
      // tarjetas ya marcadas .wsdapp), así que es seguro disparar esto en cada tanda.
      if (typeof history.replaceState === 'function') {
        history.replaceState(history.state, '', window.location.href);
      }

      // Animaciones — desactivadas mientras se restaura una sesión previa
      // (this.restoring): con 36-48 productos por tanda, el stagger normal añadiría
      // varios segundos de espera visible a algo que debe sentirse instantáneo, y además
      // retrasaría el removeAttribute('loading') que restoreUpTo() necesita para
      // encadenar la siguiente tanda sin fetches en paralelo.
      if (this.restoring) {
        addedItems.forEach((item) => {
          item.style.opacity = '1';
          item.style.transform = 'translateY(0)';
        });
        base.removeAttribute('loading');
        base.checkIfEndReached();
      } else if (this.animations_enabled && typeof gsap !== 'undefined') {
        gsap.set(addedItems, {
          opacity: 0,
          y: 30
        });
        gsap.to(addedItems, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function() {
            base.removeAttribute('loading');
            // Verificar si hemos llegado al final después de la animación
            base.checkIfEndReached();
          }
        });
      } else {
        // Animación CSS fallback
        addedItems.forEach((item, index) => {
          setTimeout(() => {
            item.style.opacity = '1';
            item.style.transform = 'translateY(0)';
          }, index * 100);
        });
        base.removeAttribute('loading');
        // Verificar si hemos llegado al final
        base.checkIfEndReached();
      }
    }
    
    checkIfEndReached() {
      // Verificar si hemos llegado al final
      // this.i ya fue incrementado en loadProducts, así que comparamos con totalPages
      if (this.i > this.totalPages) {
        //console.log('Reached end of pages');
        this.endPagination();
      }
    }
    
    initializeLazyLoading(newPosts) {
      // lazysizes (assets/vendor.min.js) vigila todo el documento en busca de elementos
      // `.lazyload` con su propio MutationObserver y los carga solo cuando se acercan al
      // viewport — los elementos añadidos aquí se detectan solos, no hace falta forzarlos.
      // Forzar `img.srcset = img.dataset.srcset` de inmediato en cada imagen añadida (como
      // se hacía antes) anula ese diferimiento: una sola tanda de scroll infinito podía
      // descargar a máxima resolución todas las imágenes de golpe, sin importar si el
      // usuario había llegado a verlas — la causa de que la pestaña colapsara en iOS.
      // checkElems() solo le pide a lazysizes que revise ahora en vez de esperar a su
      // siguiente chequeo throttled; sigue cargando solo lo que está cerca del viewport.
      if (typeof window.lazySizes !== 'undefined') {
        window.lazySizes.loader.checkElems();
      }
    }

    getStorageKey() {
      return 'ng-pagination-scroll:' + document.location.pathname + document.location.search;
    }

    saveScrollState() {
      // No persistir mientras se está restaurando una sesión previa: el scrollY todavía
      // no refleja la posición real del usuario (se restaura al final de la secuencia),
      // así que escribir aquí solo pisaría el estado que restoreScrollState() ya leyó en
      // una variable local sin ningún beneficio.
      if (this.restoring) return;
      // this.i ya apunta a la SIGUIENTE página a cargar en este punto, así que la última
      // cargada es this.i - 1. No guardar nada si no se ha avanzado más allá de la página
      // inicial servida por el servidor (no hay nada que restaurar en ese caso).
      const lastLoadedPage = this.i - 1;
      if (lastLoadedPage <= this.currentPage) return;
      try {
        sessionStorage.setItem(this.getStorageKey(), JSON.stringify({
          page: lastLoadedPage,
          scrollY: window.scrollY,
          savedAt: Date.now()
        }));
      } catch (e) {
        // Safari privado, cuota de sessionStorage llena, etc. — best effort, no bloquea nada.
      }
    }

    async restoreScrollState() {
      let saved;
      try {
        const raw = sessionStorage.getItem(this.getStorageKey());
        if (!raw) return;
        saved = JSON.parse(raw);
      } catch (e) {
        return;
      }

      if (!saved || !saved.page || saved.page <= this.currentPage) return;

      if (!saved.savedAt || (Date.now() - saved.savedAt) > PAGINATION_RESTORE_TTL_MS) {
        try {
          sessionStorage.removeItem(this.getStorageKey());
        } catch (e) { /* noop */ }
        return;
      }

      const target = Math.min(saved.page, this.totalPages);
      const autoTarget = Math.min(target, this.currentPage + PAGINATION_RESTORE_AUTO_CAP);

      this.restoring = true;
      await this.restoreUpTo(autoTarget);
      this.restoring = false;

      if (autoTarget >= target) {
        // Instantáneo, sin animación de scroll — el usuario espera volver exactamente
        // donde estaba, no ver un scroll-to-top seguido de un scroll suave hacia abajo.
        window.scrollTo(0, saved.scrollY);
        try {
          sessionStorage.removeItem(this.getStorageKey());
        } catch (e) { /* noop */ }
      } else {
        // Quedan más tandas de las que se restauran automáticamente (tope de red/memoria)
        // — en vez de seguir descargando todo el historial de golpe (la misma ráfaga que
        // causaba los crashes de memoria en iOS que motivó el resto de este archivo),
        // se ofrece una afordancia bajo demanda.
        this.showContinueAffordance(target, saved.scrollY);
      }
    }

    async restoreUpTo(targetPage) {
      while (this.i <= targetPage && this.i <= this.totalPages) {
        await this.loadProducts();
      }
    }

    showContinueAffordance(targetPage, scrollY) {
      if (this.querySelector('.pagination-restore-banner')) return;

      let message = 'Sigue donde lo dejaste: hay más productos cargados anteriormente.';
      let buttonLabel = 'Continuar donde lo dejaste';
      if (window.location.host === 'newgardenshopping.nl') {
        message = 'Ga verder waar je gebleven was: er zijn meer eerder geladen producten.';
        buttonLabel = 'Verder gaan waar je gebleven was';
      } else if (window.location.host === 'newgardenshop.pt') {
        message = 'Continue de onde parou: existem mais produtos carregados anteriormente.';
        buttonLabel = 'Continuar de onde parou';
      }

      const banner = document.createElement('div');
      banner.className = 'pagination-restore-banner';
      banner.innerHTML = `<p>${message}</p><button type="button" class="button">${buttonLabel}</button>`;

      const button = banner.querySelector('button');
      button.addEventListener('click', async () => {
        button.disabled = true;
        this.restoring = true;
        await this.restoreUpTo(targetPage);
        this.restoring = false;
        banner.remove();
        window.scrollTo(0, scrollY);
        try {
          sessionStorage.removeItem(this.getStorageKey());
        } catch (e) { /* noop */ }
      });

      // Igual que showEndMessage/showError: se inserta dentro del propio elemento
      // <pagination-theme>, que ya tiene el contexto de estilos de .pagination.
      this.appendChild(banner);
    }

    endPagination() {
      if (this.observer) {
        this.observer.unobserve(this);
        if (this.sentinel) {
          this.observer.unobserve(this.sentinel);
          if (this.sentinel.parentNode) {
            this.sentinel.parentNode.removeChild(this.sentinel);
          }
        }
        this.observer.disconnect();
      }
      this.removeAttribute('loading');
      if (this.button) {
        this.button.classList.add('visually-hidden');
      }
      this.showEndMessage();
    }
    
    showEndMessage() {
      if (!this.querySelector('.pagination-end-message')) {
      const endMessage = document.createElement('div');
      endMessage.className = 'pagination-end-message';
      
      let message = 'No hay más artículos que mostrar';
      
      if (window.location.host === 'newgardenshopping.nl') {
        message = 'Er zijn geen items meer om weer te geven';
      } else if (window.location.host === 'newgardenshop.pt') {
        message = 'Não existem mais itens para apresentar';
      }
      
      endMessage.innerHTML = `<p>${message}</p>`;
      this.appendChild(endMessage);
      }
    }
    
    showError() {
      if (!this.querySelector('.pagination-error-message')) {
        const errorMessage = document.createElement('div');
        errorMessage.className = 'pagination-error-message';
        errorMessage.innerHTML = '<p>Error al cargar más artículos. <button onclick="location.reload()">Intentar de nuevo</button></p>';
        this.appendChild(errorMessage);
      }
    }
  }
  
  customElements.define('pagination-theme', Pagination);
}

// Función helper para eventos personalizados
function dispatchCustomEvent(eventName, detail = {}) {
  const event = new CustomEvent(eventName, {
    detail: detail,
    bubbles: true,
    cancelable: true
  });
  document.dispatchEvent(event);
}

// Throttle simple para el listener de scroll que persiste la posición — no se usa
// beforeunload/pagehide para capturar el estado porque no son fiables en iOS Safari;
// es más robusto mantener el estado siempre al día mientras el usuario scrollea.
function throttle(fn, wait) {
  let lastCall = 0,
    timeoutId = null;
  return function throttled(...args) {
    const now = Date.now();
    const remaining = wait - (now - lastCall);
    if (remaining <= 0) {
      lastCall = now;
      fn.apply(this, args);
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
}